import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  UserOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import userService from "../../../services/user.service";
import orderService from "../../../services/order.service";
import { LayoutContext } from "../../providers/layout";
import OrderTable from "../../tables/order-table";
import UserTable from "../../tables/user-table";
import {
  LocalStorageUser,
  UserAddress,
} from "../../../interfaces/user.interface";
import "./users.scss";

const { Search } = Input;
const { Title, Text } = Typography;

interface UsersProps {
  history?: any;
}

const Users: React.FC<UsersProps> = ({ history }) => {
  const { setTitle, setLoading } = useContext(LayoutContext);

  const [users, setUsers] = useState<LocalStorageUser[]>([]);
  const [searchText, setSearchText] = useState("");
  const [addressModalVisible, setAddressModalVisible] = useState(false);
  const [ordersModalVisible, setOrdersModalVisible] = useState(false);
  const [userAddresses, setUserAddresses] = useState<UserAddress[]>([]);
  const [userOrders, setUserOrders] = useState<any[]>([]);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    setTitle("Users");
  }, [setTitle]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await userService.getAllUsers();
      setUsers(Array.isArray(allUsers) ? allUsers : []);
    } catch (error) {
      message.error("Users listesi alınırken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    if (!q) return users;

    return users.filter((u) => {
      const name = u.name?.toLowerCase() || "";
      const email = u.email?.toLowerCase() || "";
      const phone = u.phone?.toLowerCase() || "";
      const id = u._id?.toLowerCase() || "";
      const role = (u.role || u.userType || "").toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        id.includes(q) ||
        role.includes(q)
      );
    });
  }, [users, searchText]);

  const stats = useMemo(() => {
    const totalUsers = users.length;

    const probableStoreOwners = users.filter((u) => {
      const role = (u.role || u.userType || "").toLowerCase();
      return (
        role.includes("owner") ||
        role.includes("store") ||
        role.includes("merchant") ||
        role.includes("admin") ||
        !!u.shopName
      );
    }).length;

    const customers = totalUsers - probableStoreOwners;

    return {
      totalUsers,
      probableStoreOwners,
      customers,
    };
  }, [users]);

  const openAddressModal = (addresses: UserAddress[] = []) => {
    setUserAddresses(addresses);
    setAddressModalVisible(true);
  };

  const openOrdersModal = async (userId: string) => {
    try {
      setLoading(true);
      const allOrders = await orderService.getOrdersByUserId(userId);
      setUserOrders(Array.isArray(allOrders) ? allOrders : []);
      setOrdersModalVisible(true);
    } catch (error) {
      message.error("Kullanıcı siparişleri alınırken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setDeletingUserId(userId);
      await userService.deleteUser(userId);
      message.success("Kullanıcı kaldırıldı.");
      await fetchUsers();
    } catch (error) {
      message.error("Kullanıcı kaldırılırken bir hata oluştu.");
    } finally {
      setDeletingUserId(null);
    }
  };

  return (
    <div className="users-page">
      <div className="users-hero">
        <div>
          <Title level={2} className="users-page-title">
            Users
          </Title>
          <Text className="users-page-subtitle">
            Mağaza sahipleri ve müşteriler aynı listede. Mimari biraz garip,
            kabul. Ama ekran en azından düzgün görünsün.
          </Text>
        </div>

        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push("/users/new")}
          >
            Add New User
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="users-stats-row">
        <Col xs={24} md={8}>
          <Card className="users-stat-card">
            <div className="users-stat-icon soft-blue">
              <TeamOutlined />
            </div>
            <div>
              <Text className="users-stat-label">Total Users</Text>
              <div className="users-stat-value">{stats.totalUsers}</div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card className="users-stat-card">
            <div className="users-stat-icon soft-gold">
              <ShopOutlined />
            </div>
            <div>
              <Text className="users-stat-label">Store Owners / Admins</Text>
              <div className="users-stat-value">
                {stats.probableStoreOwners}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card className="users-stat-card">
            <div className="users-stat-icon soft-purple">
              <UserOutlined />
            </div>
            <div>
              <Text className="users-stat-label">Customers</Text>
              <div className="users-stat-value">{stats.customers}</div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="users-table-card">
        <div className="users-toolbar">
          <Search
            placeholder="Kullanıcı ara: isim, email, telefon veya rol"
            allowClear
            size="large"
            onSearch={(value) => setSearchText(value)}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            className="users-search premium-search"
          />

          <Tag className="users-result-tag">{filteredUsers.length} sonuç</Tag>
        </div>

        <UserTable
          users={filteredUsers}
          visibleAddressModal={openAddressModal}
          visibleOrdersModal={openOrdersModal}
          onDeleteUser={handleDeleteUser}
          deletingUserId={deletingUserId}
        />
      </Card>

      <Modal
        title="Saved Addresses"
        visible={addressModalVisible}
        onOk={() => setAddressModalVisible(false)}
        onCancel={() => setAddressModalVisible(false)}
        footer={null}
        width={900}
        className="users-modal"
      >
        {userAddresses?.length ? (
          <div className="address-grid">
            {userAddresses.map((ua, index) => (
              <div className="address-card" key={ua._id || index}>
                <div className="address-card-title">{ua.name || "Address"}</div>
                <div className="address-card-line">
                  <span>Address Line 1:</span> {ua.address_line_1 || "-"}
                </div>
                <div className="address-card-line">
                  <span>Address Line 2:</span> {ua.address_line_2 || "-"}
                </div>
                <div className="address-card-line">
                  <span>City:</span> {ua.city || "-"}
                </div>
                <div className="address-card-line">
                  <span>State:</span> {ua.state || "-"}
                </div>
                <div className="address-card-line">
                  <span>Zip Code:</span> {ua.zipCode || "-"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty description="Kayıtlı adres bulunamadı." />
        )}
      </Modal>

      <Modal
        title="User Orders"
        visible={ordersModalVisible}
        onOk={() => setOrdersModalVisible(false)}
        onCancel={() => setOrdersModalVisible(false)}
        footer={null}
        width="85%"
        className="users-modal"
      >
        <OrderTable orders={userOrders} />
      </Modal>
    </div>
  );
};

export default withRouter(Users);
